import React from 'react';
import {
  type CaraWorkspaceArtifactSummaryFieldsFragment,
  type VisibilityConfigFieldsFragment,
  type VisibilityConfigInput
} from '~/api/graphql';
import {
  VisibilityBadge,
  type VisibilityBadgeVariant
} from '~/components/visibility/visibility-badge';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { latestVersion } from './document-versions';

export const latestArtifactVersion = (artifact: CaraWorkspaceArtifactSummaryFieldsFragment) =>
  latestVersion(artifact.versions) ?? null;

const visibilityFromInput = (input: VisibilityConfigInput): VisibilityConfigFieldsFragment => ({
  __typename: 'VisibilityConfig',
  level: input.level,
  userIds: input.userIds ?? [],
  teamIds: input.teamIds ?? []
});

export const DocumentArtifactVisibilityControl = ({
  artifact,
  versionNumber,
  readOnlyVariant = 'detail'
}: {
  artifact: CaraWorkspaceArtifactSummaryFieldsFragment;
  versionNumber?: number;
  readOnlyVariant?: VisibilityBadgeVariant;
}) => {
  const version =
    artifact.versions.find((candidate) => candidate.version === versionNumber) ??
    latestArtifactVersion(artifact);
  const [visibility, setVisibility] = React.useState(version?.visibility ?? null);
  const visibilityEditAllowed = useVisibilityEditAllowed(artifact.metadata.agencyUser?.id);
  const { handleVisibilityUpdate } = useArtifactEditor({
    workspaceId: artifact.metadata.workspaceId,
    artifactId: artifact.id,
    version: version?.version ?? 0
  });

  React.useEffect(() => {
    setVisibility(version?.visibility ?? null);
  }, [version?.visibility, version?.version]);

  if (!version) {
    return null;
  }

  if (!visibilityEditAllowed) {
    return <VisibilityBadge visibility={visibility} variant={readOnlyVariant} />;
  }

  return (
    <VisibilityPopover
      visibility={visibility}
      onSave={async (input) => {
        await handleVisibilityUpdate(input);
        setVisibility(visibilityFromInput(input));
      }}
    />
  );
};
